import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

export interface PackageItinerary {
    id: number;
    package_id: number;
    start_time: string | null;
    end_time: string | null;
    activity: string;
    description: string | null;
    created_at?: string;
    updated_at?: string;
}

export interface Package {
    package_id: number;
    period_id: number;
    parent_id: number | null;
    code: string;
    name: string;
    description: string | null;
    price: string | number;
    is_header: boolean | number;
    photo?: string | null;
    photo_path?: string | null;
    is_active: boolean | number;
    is_quota?: boolean | number;
    quota?: number;
    created_at?: string;
    updated_at?: string;
    parent?: Package;
    children?: Package[];
    itineraries?: PackageItinerary[];
}

// GET All Packages
export const useAdminPackages = (is_header?: 0 | 1) => {
    return useQuery<Package[]>({
        queryKey: ["admin-packages", is_header],
        queryFn: async () => {
            const params = is_header !== undefined ? { is_header } : {};
            const { data } = await axiosClient.get("/packages", { params, withCredentials: true });
            return data;
        },
    });
};

// GET Single Package
export const useAdminPackage = (id: number | null) => {
    return useQuery<Package>({
        queryKey: ["admin-package", id],
        queryFn: async () => {
            const { data } = await axiosClient.get(`/packages/${id}`, { withCredentials: true });
            return data;
        },
        enabled: !!id,
    });
};

// CREATE Package
export const useCreatePackage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: FormData | Record<string, any>) => {
            // Determine if payload is FormData
            const isFormData = payload instanceof FormData;
            const headers = isFormData ? { "Content-Type": "multipart/form-data" } : {};
            
            const { data } = await axiosClient.post("/packages", payload, { 
                withCredentials: true,
                headers,
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-packages"] });
        },
    });
};

// UPDATE Package
export const useUpdatePackage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: FormData | Record<string, any> }) => {
            const isFormData = payload instanceof FormData;
            let url = `/packages/${id}`;
            const headers: any = {};
            
            if (isFormData) {
                // Laravel needs _method=PUT when sending FormData via POST
                url = `/packages/${id}?_method=PUT`;
                headers["Content-Type"] = "multipart/form-data";
                const { data } = await axiosClient.post(url, payload, { withCredentials: true, headers });
                return data;
            } else {
                const { data } = await axiosClient.put(url, payload, { withCredentials: true });
                return data;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-packages"] });
            queryClient.invalidateQueries({ queryKey: ["admin-package"] });
        },
    });
};

// DELETE Package
export const useDeletePackage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`/packages/${id}`, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-packages"] });
        },
    });
};

// CREATE Package Itinerary (rincian jadwal: jam & aktivitas)
export const useCreatePackageItinerary = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ packageId, payload }: { packageId: number; payload: Partial<PackageItinerary> }) => {
            const { data } = await axiosClient.post(`/packages/${packageId}/itineraries`, payload, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-packages"] });
            queryClient.invalidateQueries({ queryKey: ["admin-package"] });
        },
    });
};

// UPDATE Package Itinerary
export const useUpdatePackageItinerary = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ packageId, id, payload }: { packageId: number; id: number; payload: Partial<PackageItinerary> }) => {
            const { data } = await axiosClient.put(`/packages/${packageId}/itineraries/${id}`, payload, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-packages"] });
            queryClient.invalidateQueries({ queryKey: ["admin-package"] });
        },
    });
};

// DELETE Package Itinerary
export const useDeletePackageItinerary = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ packageId, id }: { packageId: number; id: number }) => {
            const { data } = await axiosClient.delete(`/packages/${packageId}/itineraries/${id}`, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-packages"] });
            queryClient.invalidateQueries({ queryKey: ["admin-package"] });
        },
    });
};
